import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductoVariantAttributeApiService } from '../services/producto-variant-attribute-api.service';
import {
  ProductVariantAttributeResponse,
  ProductVariantAttributeValueResponse
} from '../models/producto-variant-attribute.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-producto-variant-attributes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './producto-variant-attributes.component.html'
})
export class ProductoVariantAttributesComponent implements OnInit {
  @Input() productId!: number;
  @Input() businessId!: number;

  attributes: ProductVariantAttributeResponse[] = [];
  loading = false;
  error = '';

  // New attribute form
  showNewAttrForm = false;
  newAttrName = '';
  newAttrValues = '';
  savingAttr = false;

  // Add value per attribute
  addingValueForAttrId: number | null = null;
  newValueText = '';
  savingValue = false;

  constructor(
    private productApi: ProductoVariantAttributeApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarAtributos();
  }

  cargarAtributos(): void {
    this.loading = true;
    this.productApi.getProductVariantAttributes(this.productId).subscribe({
      next: (data) => {
        this.attributes = data;
        this.loading = false;
      },
      error: () => {
        this.attributes = [];
        this.loading = false;
      }
    });
  }

  abrirNuevoAtributo(): void {
    this.newAttrName = '';
    this.newAttrValues = '';
    this.showNewAttrForm = true;
  }

  cerrarNuevoAtributo(): void {
    this.showNewAttrForm = false;
  }

  guardarAtributo(): void {
    if (!this.newAttrName.trim()) return;
    this.savingAttr = true;
    const values = this.newAttrValues
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0);

    this.productApi.createProductVariantAttribute(this.productId, {
      name: this.newAttrName.trim(),
      values
    }, this.businessId).subscribe({
      next: () => {
        this.savingAttr = false;
        this.cerrarNuevoAtributo();
        this.cargarAtributos();
      },
      error: (err) => {
        this.savingAttr = false;
        this.error = err.error?.message || 'Error al crear el atributo';
      }
    });
  }

  async eliminarAtributo(attrId: number): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar atributo',
      message: '¿Eliminar este atributo y todos sus valores?'
    });
    if (!ok) return;
    this.productApi.deleteProductVariantAttribute(this.productId, attrId, this.businessId).subscribe({
      next: () => {
        this.cargarAtributos();
        this.toast.deleted('Atributo');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar el atributo';
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el atributo' });
      }
    });
  }

  abrirAgregarValor(attrId: number): void {
    this.addingValueForAttrId = attrId;
    this.newValueText = '';
  }

  cerrarAgregarValor(): void {
    this.addingValueForAttrId = null;
    this.newValueText = '';
  }

  guardarValor(attrId: number): void {
    if (!this.newValueText.trim()) return;
    this.savingValue = true;
    this.productApi.addProductVariantAttributeValue(this.productId, attrId, {
      value: this.newValueText.trim()
    }, this.businessId).subscribe({
      next: () => {
        this.savingValue = false;
        this.cerrarAgregarValor();
        this.cargarAtributos();
      },
      error: (err) => {
        this.savingValue = false;
        this.error = err.error?.message || 'Error al agregar el valor';
      }
    });
  }

  async eliminarValor(attrId: number, valueId: number): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar valor',
      message: '¿Eliminar este valor?'
    });
    if (!ok) return;
    this.productApi.deleteProductVariantAttributeValue(this.productId, attrId, valueId, this.businessId).subscribe({
      next: () => {
        this.cargarAtributos();
        this.toast.deleted('Valor de atributo');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar el valor';
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el valor' });
      }
    });
  }
}
